import { type CSSProperties, memo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SvgIconComponent } from '@mui/icons-material';
import { Close, Save, TableRows } from '@mui/icons-material';
import type { Row, TableOptions, VisibilityState } from '@tanstack/react-table';
import { Collapse, TableRow as MuiTableRow, Tooltip } from '@mui/material';
import { CollapseButton, CollapseCell, DragCell, EditButtonsWrapper, MetaCellContent, StyledTableCell, StyledTableRow } from './TableRow.styles';

import type { DataSchema, HeliosRowControls, HeliosRowFunctionsControls, Schema } from 'types';
import { TableCell } from './TableCell';
import { TableCellEdit } from './TableCellEdit';
import { TableCellEditHelios } from './TableCellEditHelios';
import { TableRowControls } from './TableRowControls';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'components/mui/common/Checkbox';
import { IconButton } from 'components/mui/common/IconButton';
import { TableCellHelios } from './TableCellHelios';
import { TableRowHeliosFunctionsControls } from './TableRowHeliosFunctionsControls';
import { TABLE_CELL_TYPE, VISIBILITY_STATE } from 'types/enums';
import { getFormFieldName } from './editableComponentFactory';
import { useTableManagement } from '../../../contexts/TableManagementContext';
import type { RowData } from '../../../utils/helios/row';
import { transformRowFieldData } from '../../../utils/helios/row';
import type { TableFormData } from './Table';
import type { RowControls } from 'types/table';
import { getSelectRowId, type TableColumnAggregationState } from 'utils/table';
import { replaceKey } from 'utils/formItemsDots';
import type { VisibilityResult } from 'types/records.ts';

interface Props<T extends keyof P, P extends DataSchema> {
  selectAllForm: UseFormReturn<TableFormData<boolean>>;
  agendaId?: string;
  row: Row<Schema<T, P>>;
  selectedRow: boolean;
  handleRowSelect: (identifier: string, isChecked: boolean) => void;
  dataSchema: P;
  numberOfVisibleRows: number;
  tableIndex: number;
  cellClicked?: (row: TableOptions<Schema<T, P>>['data'], cell: keyof P) => void;
  entireRowClicked?: (row: TableOptions<Schema<T, P>>['data'][number]) => void;
  showRowClickStyles?: boolean;
  editRowId?: string[] | null;
  onSaveEditedRow?: (
    row: {
      [key in keyof P]: {
        value: string | number | boolean | object;
        type: TABLE_CELL_TYPE;
      };
    } extends {
      id: {
        value: string;
        type: TABLE_CELL_TYPE.ID;
      };
    }
      ? {
          [key in keyof P]: {
            value: string | number | boolean | object;
            type: TABLE_CELL_TYPE;
          };
        }
      : {
          [key in keyof P]: {
            value: string | number | boolean | object;
            type: TABLE_CELL_TYPE;
          };
        } & {
          id: {
            value: string;
            type: TABLE_CELL_TYPE.ID;
          };
        }
  ) => void;
  innerTable?: (row: TableOptions<Schema<T, P>>['data'][number], tableIndex: number) => React.ReactNode;
  tableControls?: {
    [key: string]: {
      visible: boolean;
      disabled?: boolean;
      onClick: () => void;
      icon: SvgIconComponent;
      label: string;
      id: string;
    };
  };

  rowControls?: RowControls<TableOptions<Schema<T, P>>['data'][number]>;
  heliosRowControls?: HeliosRowControls<TableOptions<Schema<T, P>>['data'][number]>;
  heliosRowFunctionsControls?: HeliosRowFunctionsControls[];

  clickableCellsKeys: (keyof P)[] | 'all';
  hideCheckbox?: boolean;
  onCancelEditRow?: (id: string) => void;
  isHeliosTable?: boolean;
  columnVisibility?: VisibilityState;
  columnAggregation?: TableColumnAggregationState;
  visibilityResults?: Record<string, VisibilityResult>;
  dataTestIdPrefix?: string;
  testIdPrefix?: string;
}

export const TableRowBase = <T extends keyof P, P extends DataSchema>({
  selectAllForm,
  agendaId,
  row,
  selectedRow,
  handleRowSelect,
  entireRowClicked,
  showRowClickStyles = true,
  dataSchema,
  onSaveEditedRow,
  innerTable,
  tableIndex,
  rowControls,
  heliosRowControls,
  heliosRowFunctionsControls,
  tableControls,
  clickableCellsKeys,
  hideCheckbox,
  onCancelEditRow,
  isHeliosTable,
  columnAggregation,
  visibilityResults,
  dataTestIdPrefix,
  testIdPrefix,
}: Props<T, P>) => {
  const resolvedTestIdPrefix = dataTestIdPrefix ?? testIdPrefix;
  const { t } = useTranslation();
  const { transform, transition, setNodeRef, isDragging, listeners, attributes } = useSortable({
    id: row.original.identifier,
  });

  const [isInnerTableOpen, setIsInnerTableOpen] = useState(false);

  const { control, handleSubmit, setValue, getValues } = useFormContext();
  const { setValue: selectAllSetValue } = selectAllForm;

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : undefined,
    position: 'relative',
  };

  const {
    hasInnerTable,
    shouldShowRowControls,
    hasHeliosFunctions,
    hasRowEditableCells,
    isCellEditable,
    isRowNew,
    hasEditableActions,
    draggable,
    showSaveButton,
    showCancelEditButton,
  } = useTableManagement();

  const isNew = isRowNew(row.original.identifier);
  const isEditing = hasRowEditableCells(row.original.identifier);

  const visibleCellsCount = row.getVisibleCells().filter((cell) => cell.column.id !== 'id' && cell.column.id !== 'hidden').length;

  const originalRow = row?.original as TableOptions<Schema<T, P>>['data'][number] & { id: string; actions?: { copyInRow?: boolean; copy?: boolean } };

  const metaColumnsCount = Number(shouldShowRowControls) + Number(hasHeliosFunctions);

  const numberOfAllCells =
    visibleCellsCount +
    (!hideCheckbox ? 1 : 0) +
    (hasInnerTable ? 1 : 0) +
    (metaColumnsCount > 0 ? 1 : 0) +
    (draggable ? 1 : 0) +
    (hasEditableActions ? 1 : 0);

  const showSaveRowButton = showSaveButton(row.original.identifier);
  const showCancelEditRowButton = showCancelEditButton(row.original.identifier);

  const stickyBgSx = {
    backgroundColor: 'background.paper',
    ...(hasInnerTable
      ? { 'tr:nth-of-type(4n-1) > &': { backgroundColor: 'background.default' } }
      : { 'tr:nth-of-type(even) > &': { backgroundColor: 'background.default' } }),
  };

  return (
    <>
      <StyledTableRow
        hasInnerTable={hasInnerTable}
        isClickable={showRowClickStyles}
        hover={showRowClickStyles}
        selected={selectedRow}
        data-index={row.index}
        data-testid={
          resolvedTestIdPrefix ? `${resolvedTestIdPrefix}-${isEditing ? 'editing-row' : 'row'}` : isEditing ? 'table-row-editing' : 'table-row'
        }
        style={style}
        ref={setNodeRef}
        onClick={(e) => {
          e.stopPropagation();
        }}>
        {!hideCheckbox && (
          <StyledTableCell sx={{ width: 'calc(var(--col-selection-size) * 1px)', padding: 0 }}>
            <Checkbox
              key={row.original.identifier}
              checked={selectedRow}
              data-testid={resolvedTestIdPrefix ? `${resolvedTestIdPrefix}-row-${row.index + 1}-checkbox` : 'table-row-select-checkbox'}
              onChange={(e) => {
                e.stopPropagation();

                const isChecked = e.target.checked;
                const selectedId = getSelectRowId(row.original, Boolean(isHeliosTable));

                handleRowSelect(selectedId, isChecked);

                if (!isChecked) {
                  selectAllSetValue('selectAll', false, {
                    shouldDirty: true,
                  });
                }
              }}
            />
          </StyledTableCell>
        )}
        {hasInnerTable && (
          <StyledTableCell sx={{ width: 'calc(var(--col-meta-size) * 1px)' }}>
            <CollapseButton onClick={() => setIsInnerTableOpen(!isInnerTableOpen)} open={isInnerTableOpen} />
          </StyledTableCell>
        )}
        {row.getVisibleCells().map((cell) => {
          if (cell.column.id === 'id' || cell.column.id === 'hidden') return;

          const modifiedCell = {
            ...cell,
            column: { ...cell.column, id: isHeliosTable ? replaceKey(cell.column.id) : cell.column.id },
          };

          const isDisabled = Boolean('disabled' in row.original && row.original.disabled);
          const cellKey = getFormFieldName(row, modifiedCell.column.id);
          const visibilityResult = visibilityResults?.[cellKey];
          const isHidden = visibilityResult?.visibilityState === VISIBILITY_STATE.HIDDEN;
          const editMode = isCellEditable(row.original.identifier, modifiedCell.column.id) && !isHidden;

          const formItemUiDefinition = visibilityResult?.formItemUiDefinition;

          if (editMode) {
            if (isHeliosTable) {
              return (
                <TableCellEditHelios
                  setValue={setValue}
                  row={row}
                  cell={modifiedCell}
                  key={cellKey}
                  control={control}
                  dataSchema={dataSchema}
                  isNew={isNew}
                  entireRowClicked={entireRowClicked}
                  columnAggregation={columnAggregation}
                />
              );
            }
            return (
              <TableCellEdit
                agendaId={agendaId}
                cell={cell}
                row={row}
                key={cellKey}
                control={control}
                dataSchema={dataSchema}
                entireRowClicked={entireRowClicked}
                columnAggregation={columnAggregation}
                formItemUiDefinition={formItemUiDefinition}
              />
            );
          }

          if (isHeliosTable) {
            return (
              <TableCellHelios
                cell={modifiedCell}
                row={row}
                dataSchema={dataSchema}
                entireRowClicked={entireRowClicked}
                key={cellKey}
                isInnerTableOpen={isInnerTableOpen}
                clickableCellsKeys={clickableCellsKeys}
                disabled={isDisabled}
                columnAggregation={columnAggregation}
              />
            );
          }

          return (
            <TableCell
              cell={cell}
              row={row}
              entireRowClicked={entireRowClicked}
              key={cellKey}
              isInnerTableOpen={isInnerTableOpen}
              clickableCellsKeys={clickableCellsKeys}
              disabled={isDisabled}
              columnAggregation={columnAggregation}
            />
          );
        })}
        {metaColumnsCount > 0 && (
          <StyledTableCell
            align="right"
            sx={{
              width: `calc(var(--col-meta-size) * ${metaColumnsCount + Number(shouldShowRowControls)}px)`,
              minWidth: `calc(var(--col-meta-size) * ${metaColumnsCount + Number(shouldShowRowControls)}px)`,
              whiteSpace: 'nowrap',
              position: 'sticky',
              right: (hasEditableActions ? 100 : 0) + (draggable ? 40 : 0),
              zIndex: 1,
              ...stickyBgSx,
              boxShadow: '-6px 0 8px -4px rgba(0,0,0,0.15)',
            }}>
            <MetaCellContent>
              {shouldShowRowControls && (
                <TableRowControls
                  row={row.original}
                  rowControls={rowControls}
                  tableControls={tableControls}
                  heliosRowControls={heliosRowControls}
                  testIdPrefix={resolvedTestIdPrefix}
                  noTableCell
                />
              )}
              {hasHeliosFunctions && (
                <TableRowHeliosFunctionsControls
                  row={originalRow}
                  controls={heliosRowFunctionsControls}
                  testIdPrefix={resolvedTestIdPrefix}
                  noTableCell
                />
              )}
            </MetaCellContent>
          </StyledTableCell>
        )}
        {draggable && (
          <DragCell
            align="right"
            sx={{ width: 'calc(var(--col-meta-size) * 1px)', position: 'sticky', right: hasEditableActions ? 100 : 0, zIndex: 1, ...stickyBgSx }}
            {...attributes}
            {...listeners}>
            <TableRows />
          </DragCell>
        )}
        {hasEditableActions && (
          <StyledTableCell
            align="right"
            sx={{ gap: '8px', width: 'calc(var(--col-actions-size) * 1px)', position: 'sticky', right: 0, zIndex: 1, ...stickyBgSx }}>
            <EditButtonsWrapper>
              {showSaveRowButton && (
                <IconButton
                  onClick={() => {
                    const rowId = row.original.identifier;
                    const values = getValues()[rowId];
                    const keys = Object.keys(values);

                    handleSubmit(() => {
                      const data: RowData<P> = {} as RowData<P>;

                      keys.forEach((key) => {
                        const value = values[key];
                        data[key as keyof P] = transformRowFieldData(key, value, dataSchema);
                      });

                      if (onSaveEditedRow) {
                        onSaveEditedRow({
                          ...data,
                          id: {
                            value: row.original.identifier,
                            type: TABLE_CELL_TYPE.ID,
                          },
                        });
                      }
                    })();
                  }}>
                  <Tooltip title={t('common.save')}>
                    <Save />
                  </Tooltip>
                </IconButton>
              )}
              {showCancelEditRowButton && (
                <IconButton onClick={() => onCancelEditRow?.(row.original.identifier)}>
                  <Tooltip title={t('common.cancel')}>
                    <Close />
                  </Tooltip>
                </IconButton>
              )}
            </EditButtonsWrapper>
          </StyledTableCell>
        )}
      </StyledTableRow>
      {hasInnerTable && (
        <MuiTableRow>
          <CollapseCell colSpan={numberOfAllCells}>
            <Collapse in={isInnerTableOpen} timeout="auto">
              {innerTable?.(row.original, tableIndex + 1)}
            </Collapse>
          </CollapseCell>
        </MuiTableRow>
      )}
    </>
  );
};

export const TableRow = memo(TableRowBase) as typeof TableRowBase;
